import { Pagination } from 'src/api/common';
import api from './baseApi';

export type CommunicationChannel = 'SMS' | 'EMAIL';

export type CommunicationStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'PARTIALLY_SENT'
  | 'FAILED'
  | 'CANCELLED';

export type DeliveryStatus = 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED';

export type CommunicationRecipient = {
  name?: string;
  phone?: string;
  email?: string;
  smsStatus?: DeliveryStatus;
  smsError?: string;
  emailStatus?: DeliveryStatus;
  emailError?: string;
};

export type CommunicationMessage = {
  _id: string;
  name?: string;
  channels: CommunicationChannel[];
  sms?: {
    message: string;
    sender?: string;
  };
  email?: {
    subject: string;
    body: string;
    isHtml?: boolean;
    replyToEmail?: string;
  };
  audience?: {
    type: 'EXPLICIT' | 'OTH_REGISTRATIONS';
    recipients?: CommunicationRecipient[];
    tribe_id?: string;
  };
  recipients?: CommunicationRecipient[];
  status: CommunicationStatus;
  sentCount?: number;
  failedCount?: number;
  recipientCount?: number;
  error?: string;
  sentAt?: string;
  scheduledAt?: string;
  sendAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

type ApiEnvelope<T> = {
  result: T;
};

type PagedApiEnvelope<T> = ApiEnvelope<T> & {
  pagination: Pagination;
};

export type ListCommunicationsParams = {
  page?: number;
  pageSize?: number;
  channel?: CommunicationChannel;
  status?: CommunicationStatus;
  tribe_id?: string;
};

export type CreateCommunicationPayload = {
  name?: string;
  channels: CommunicationChannel[];
  sms?: {
    message: string;
    sender?: string;
  };
  email?: {
    subject: string;
    body: string;
    isHtml?: boolean;
    replyToEmail?: string;
  };
  audience:
    | {
        type: 'EXPLICIT';
        tribe_id?: string;
        recipients: CommunicationRecipient[];
      }
    | {
        type: 'OTH_REGISTRATIONS';
        tribe_id?: string;
      };
  sendAt?: string;
};

export async function listCommunications({
  page = 1,
  pageSize = 20,
  channel,
  status,
  tribe_id,
}: ListCommunicationsParams = {}): Promise<{
  result: CommunicationMessage[];
  pagination: Pagination;
}> {
  const { data } = await api.get<PagedApiEnvelope<CommunicationMessage[]>>(
    'communications',
    {
      params: {
        page,
        pageSize,
        channel,
        status,
        tribe_id,
      },
    }
  );

  return { result: data.result, pagination: data.pagination };
}

export async function getCommunication(
  communicationId: string
): Promise<CommunicationMessage> {
  const { data } = await api.get<ApiEnvelope<CommunicationMessage>>(
    `communications/${encodeURIComponent(communicationId)}`
  );

  return data.result;
}

export async function createCommunication(
  payload: CreateCommunicationPayload
): Promise<CommunicationMessage> {
  const { data } = await api.post<ApiEnvelope<CommunicationMessage>>(
    'communications',
    payload
  );

  return data.result;
}

export async function updateCommunication(
  communicationId: string,
  payload: CreateCommunicationPayload
): Promise<CommunicationMessage> {
  const { data } = await api.patch<ApiEnvelope<CommunicationMessage>>(
    `communications/${encodeURIComponent(communicationId)}`,
    payload
  );

  return data.result;
}

export async function sendCommunication(
  communicationId: string
): Promise<CommunicationMessage> {
  const { data } = await api.post<ApiEnvelope<CommunicationMessage>>(
    `communications/${encodeURIComponent(communicationId)}/send`
  );

  return data.result;
}

export async function cancelCommunication(
  communicationId: string
): Promise<CommunicationMessage> {
  const { data } = await api.post<ApiEnvelope<CommunicationMessage>>(
    `communications/${encodeURIComponent(communicationId)}/cancel`
  );

  return data.result;
}
